import React from "react";
import { logException } from "~/lib/sentry.js";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };

    this.isDev =
      window &&
      window.location &&
      window.location.hostname.indexOf("localhost") !== -1;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  static isChunkLoadError(error) {
    const msg = String(error?.message ?? "");
    return (
      msg.includes("error loading dynamically imported module") ||
      msg.includes("is not a valid JavaScript MIME type")
    );
  }

  componentDidCatch(error, errorInfo) {
    // Chunk-load failures happen when a new deployment is served while the
    // user still has the old page open. A hard reload fetches fresh chunks.
    // Guard against infinite reload loops with a sessionStorage flag.
    if (ErrorBoundary.isChunkLoadError(error)) {
      const reloadKey = "chunk_error_reload";
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
        return;
      }
      // Reload didn't fix it — fall through to show the error UI and report.
      sessionStorage.removeItem(reloadKey);
    }
    // Log error using sentry.js utility
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    logException(error, errorInfo?.componentStack);
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    // Memoize isDev calculation
    if (this.isDev) {
      return this.props.children;
    }
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            background: "#fff",
            color: "#222",
          }}
        >
          <h1>Sorry, app crash.</h1>
          <p>An unexpected error occured.</p>
          <button
            onClick={this.handleRefresh}
            style={{
              marginTop: 24,
              padding: "10px 24px",
              fontSize: 18,
              borderRadius: 6,
              border: "1px solid #ccc",
              background: "#f5f5f5",
              cursor: "pointer",
            }}
          >
            Refresh page
          </button>

          <p style={{ marginTop: 24, maxWidth: 400, textAlign: "center" }}>
            Our team has been automatically notified. However, if you could
            share details about what you were doing when the error occurred, it
            would help us a lot. Please email us at{" "}
            <a href="mailto:contact@gliding.app">contact@gliding.app</a>.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
